import {
  DestroyRef,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  output,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { UntypedFormGroup } from '@angular/forms';
import { UntypedFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';

import { combineLatest, distinctUntilChanged, of, startWith, switchMap } from 'rxjs';

import type { FullCandidate } from '@core/candidate-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { CandidateInterviewTemplatesService } from '@core/interview-templates-logic';
import { AppStore } from '@core/legacy-app-store-legacy';
import { OfferSelectComponent } from '@core/offers-smart-ui';
import { OfferKind } from '@core/offers-types';
import type { OfferSelectOption } from '@core/offers-ui';
import {
  BadgeComponent,
  SelectGroupedOptions,
  ButtonComponent,
  GroupComponent,
  DividerComponent,
  SelectComponent,
  LabelComponent,
  FormFieldComponent,
  HeadingComponent,
} from '@core/user-interface-legacy';

import type { AddEvaluationsDropdownModuleTranslations } from '../../translations';

@Component({
  selector: 'rt-evaluation-forms-tab',
  templateUrl: 'evaluation-forms-tab.component.html',
  styleUrls: ['./evaluation-forms-tab.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeadingComponent,
    ReactiveFormsModule,
    FormFieldComponent,
    LabelComponent,
    OfferSelectComponent,
    SelectComponent,
    DividerComponent,
    GroupComponent,
    ButtonComponent,
    TranslatePipe,
    BadgeComponent,
  ],
})
export class EvaluationFormsTabComponent {
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly appStore = inject(AppStore);
  private readonly candidateInterviewTemplatesService = inject(CandidateInterviewTemplatesService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  public readonly offers = input<OfferSelectOption[]>([]);
  public readonly hasOffers = input<boolean>(false);
  public readonly schedule = input<boolean>(false);
  public readonly fromHiddenEvaluations = input<boolean>(false);
  public readonly canManageInterviewTemplates = input<boolean>(undefined!);
  public readonly translations = input<AddEvaluationsDropdownModuleTranslations>(undefined!);
  public readonly candidate = input<FullCandidate>();
  public readonly candidateId = input<number>();
  public readonly allTemplates = input<SelectGroupedOptions[]>([]);

  public submit = output<{
    talentPoolId?: number;
    offerId?: number;
    templateId: string;
  }>();
  public close = output<void>();

  public form: UntypedFormGroup;

  protected visibleTemplates = signal<SelectGroupedOptions[]>([]);
  protected templatesLoaded = signal<boolean>(false);

  constructor() {
    this.form = this.formBuilder.group({
      offerId: [''],
      templateId: ['', Validators.required],
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.hasOffers() && this.form.get('offerId')!.value === '') {
      const [firstOffer] = this.offers();
      this.form.get('offerId')!.setValue(firstOffer!.id);
    }
  }

  public ngOnInit(): void {
    this.fetchTemplatesOnOfferChange();
  }

  public hasMoreThanOneOffer(): boolean {
    return this.offers().length > 1;
  }

  public addInterview(): void {
    this.appStore.dispatch({ type: 'createEvaluation' });

    const { offerId, templateId } = this.form.value;
    const isTalentPool =
      this.offers().find(offer => offerId === offer.id)?.kind === OfferKind.TalentPool;

    this.submit.emit({
      templateId,
      ...(isTalentPool ? { talentPoolId: offerId } : { offerId }),
    });
  }

  private fetchTemplatesOnOfferChange(): void {
    const candidateId = this.candidate()?.id || this.candidateId();

    this.form
      .get('offerId')!
      .valueChanges.pipe(
        startWith(this.form.get('offerId')!.value),
        distinctUntilChanged(),
        switchMap(offerId => {
          const isTalentPool =
            this.offers().find(offer => offerId === offer.id)?.kind === OfferKind.TalentPool;

          if (!isTalentPool && offerId && candidateId) {
            return combineLatest({
              result: this.candidateInterviewTemplatesService.fetchGroupedCandidateTemplates({
                candidateId,
                offerId,
                kind: 'interview',
              }),
              loaded: this.candidateInterviewTemplatesService.areCandidateTemplatesLoaded({
                candidateId,
                offerId,
                kind: 'interview',
              }),
            });
          }

          const fallbackGroups = this.allTemplates();
          return of({
            result: {
              groups: fallbackGroups,
              preselectedTemplateId: null,
            },
            loaded: true,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ result, loaded }) => {
        this.visibleTemplates.set(result.groups);
        this.templatesLoaded.set(loaded);
        const templateIdControl = this.form.get('templateId')!;
        if (!templateIdControl.value) {
          templateIdControl.setValue(
            result.preselectedTemplateId !== null ? result.preselectedTemplateId : '',
          );
        }
        this.cdRef.detectChanges();
      });
  }
}
